/**
 * Monthly voice credits per plan, shared by every paid voice.
 *
 * reading     — everyday book narration on Google Cloud WaveNet (en-US-Wavenet-J),
 *               metered in characters, which is what Google bills.
 * performance — premium narration on Gemini Flash-Lite TTS (via OpenRouter),
 *               metered in seconds of audio (Gemini bills audio output tokens).
 * studio      — ElevenLabs Flash for Jarvis lines; expressive = ElevenLabs v3.
 *               Metered in characters.
 *
 * Every listen burns credits at its voice's real 2027 provider cost, whether or
 * not the audio was cached (1 credit = $0.001). Piper is free and unmetered
 * (bounded by its render caps). When credits run out, Piper reads.
 */
import { PRELAUNCH, type Plan } from './plans';
import { PLAN_CAPS } from './planCaps';

export type VoiceKind = 'reading' | 'performance' | 'studio' | 'expressive';

/** ~150 words/min × ~6 characters/word (spaces and punctuation included). */
export const READING_CHARS_PER_HOUR = 54_000;

// List prices, US$ (verified 2026-10-20, docs/ai-model-costs.md).
/** cloud.google.com/text-to-speech/pricing — WaveNet, per 1M characters. */
export const WAVENET_USD_PER_MILLION_CHARS = 4;
/** cloud.google.com/text-to-speech/pricing — Neural2, per 1M characters (no longer the default). */
export const NEURAL2_USD_PER_MILLION_CHARS = 16;
/** openrouter.ai/google/gemini-3.8-flash-lite-tts — per 1M audio output tokens / text input tokens (2026). */
export const GEMINI_USD_PER_MILLION_AUDIO_TOKENS = 6;
export const GEMINI_USD_PER_MILLION_TEXT_TOKENS = 0.5;
/** The same model from 2027-01-01, which is what credits are priced at. */
export const GEMINI_2027_USD_PER_MILLION_AUDIO_TOKENS = 12;
export const GEMINI_2027_USD_PER_MILLION_TEXT_TOKENS = 1;
/** OpenRouter's fee on credit purchases. */
export const OPENROUTER_FEE = 0.055;
/** Google: audio tokens correspond to 25 tokens per second of audio. */
export const GEMINI_AUDIO_TOKENS_PER_SECOND = 25;
/** ElevenLabs API per 1K characters: v3 (expressive) and Flash v2.5 (Jarvis realtime). */
export const ELEVENLABS_USD_PER_THOUSAND_CHARS = 0.08;
export const ELEVENLABS_FLASH_USD_PER_THOUSAND_CHARS = 0.04;

/** Credits are stored in thousandths so short lines aren't rounded up to a whole credit. */
export const MILLI = 1000;
/** USD per credit. */
export const USD_PER_CREDIT = 0.001;

const GEMINI_2027_USD_PER_SECOND =
  (GEMINI_AUDIO_TOKENS_PER_SECOND * GEMINI_2027_USD_PER_MILLION_AUDIO_TOKENS +
    (READING_CHARS_PER_HOUR / 4 / 3600) * GEMINI_2027_USD_PER_MILLION_TEXT_TOKENS) / 1_000_000 * (1 + OPENROUTER_FEE);

/** Milli-credits per metered unit (character, or second for performance). */
export const VOICE_CREDIT_RATES: Record<VoiceKind, number> = {
  reading: (WAVENET_USD_PER_MILLION_CHARS / 1_000_000 / USD_PER_CREDIT) * MILLI,
  performance: (GEMINI_2027_USD_PER_SECOND / USD_PER_CREDIT) * MILLI,
  studio: (ELEVENLABS_FLASH_USD_PER_THOUSAND_CHARS / 1000 / USD_PER_CREDIT) * MILLI,
  expressive: (ELEVENLABS_USD_PER_THOUSAND_CHARS / 1000 / USD_PER_CREDIT) * MILLI,
};

/** Milli-credits a listen of `amount` units costs (always at least 1 for a non-empty listen). */
export function creditsFor(kind: VoiceKind, amount: number): number {
  if (!(amount > 0)) return 0;
  return Math.max(1, Math.ceil(amount * VOICE_CREDIT_RATES[kind]));
}

/** Hours of `kind` that `milliCredits` buy (studio/expressive at reading pace). */
export function creditsToHours(milliCredits: number, kind: VoiceKind): number {
  const perHour = kind === 'performance' ? VOICE_CREDIT_RATES.performance * 3600 : VOICE_CREDIT_RATES[kind] * READING_CHARS_PER_HOUR;
  return Math.max(0, milliCredits) / perHour;
}

/** Monthly budget per plan, in credits (planCaps.ts). */
export const VOICE_CREDITS: Record<Plan, number> = { FREE: 0, PRO: PLAN_CAPS.PRO.voiceCredits, ELITE: PLAN_CAPS.ELITE.voiceCredits };

export function voiceCreditCap(plan: Plan): number {
  return Math.round(VOICE_CREDITS[plan] * MILLI);
}

const hours1 = (h: number) => Math.round(h * 10) / 10;
/** The whole monthly budget spent on one voice, for copy ("about 8 h of WaveNet"). */
export const READING_HOURS: Record<Plan, number> = {
  FREE: 0, PRO: hours1(creditsToHours(voiceCreditCap('PRO'), 'reading')), ELITE: hours1(creditsToHours(voiceCreditCap('ELITE'), 'reading')),
};
export const PERFORMANCE_HOURS: Record<Plan, number> = {
  FREE: 0, PRO: hours1(creditsToHours(voiceCreditCap('PRO'), 'performance')), ELITE: hours1(creditsToHours(voiceCreditCap('ELITE'), 'performance')),
};

/** Calendar month (UTC) an allowance belongs to, e.g. "2026-10". */
export function usageMonth(at: Date = new Date()): string {
  return at.toISOString().slice(0, 7);
}

/** Plan whose allowance applies. Before launch every signed-in account gets Elite's. */
export function allowancePlan(plan: Plan, prelaunch: boolean = PRELAUNCH): Plan {
  return prelaunch ? 'ELITE' : plan;
}

/**
 * Whether another line may start. A line that starts under the cap is allowed
 * to finish past it, so a paragraph is never cut mid-sentence.
 */
export function hasRoom(used: number, cap: number): boolean {
  return cap > 0 && used < cap;
}

export function charsToHours(chars: number): number {
  return chars / READING_CHARS_PER_HOUR;
}

export function wavenetCostUsd(chars: number): number {
  return (chars / 1_000_000) * WAVENET_USD_PER_MILLION_CHARS;
}

export function neural2CostUsd(chars: number): number {
  return (chars / 1_000_000) * NEURAL2_USD_PER_MILLION_CHARS;
}

/** Gemini cost for `seconds` of speech from `chars` of text (~4 characters per text token). */
export function geminiCostUsd(seconds: number, chars: number = (seconds / 3600) * READING_CHARS_PER_HOUR): number {
  const audioTokens = seconds * GEMINI_AUDIO_TOKENS_PER_SECOND;
  const textTokens = chars / 4;
  return (audioTokens / 1_000_000) * GEMINI_USD_PER_MILLION_AUDIO_TOKENS + (textTokens / 1_000_000) * GEMINI_USD_PER_MILLION_TEXT_TOKENS;
}

export function studioCostUsd(chars: number, kind: 'studio' | 'expressive' = 'expressive'): number {
  return (chars / 1000) * (kind === 'studio' ? ELEVENLABS_FLASH_USD_PER_THOUSAND_CHARS : ELEVENLABS_USD_PER_THOUSAND_CHARS);
}

/** Provider cost (2027) if a user spends the whole monthly budget, on any mix of voices. */
export function worstCaseMonthlyCostUsd(plan: Plan): number {
  return (voiceCreditCap(plan) / MILLI) * USD_PER_CREDIT;
}
